import type { Ssm5FreezeFrameReadout } from '@packages/domain/ssm5';

/** The UTC date parts used to write the event detail in the design's format. */
const EVENT_DATE_FORMAT = new Intl.DateTimeFormat('en-US', {
    day: '2-digit',
    hour: '2-digit',
    hourCycle: 'h23',
    minute: '2-digit',
    month: '2-digit',
    timeZone: 'UTC',
    year: 'numeric',
});

/**
 * Write when and where the event was recorded, leaving missing parts and their commas out.
 *
 * @param readout - the event date and place reported with the read
 * @returns the event detail in UTC, or undefined when neither part was reported
 */
export function freezeFrameEventDetail(
    readout: Pick<Ssm5FreezeFrameReadout, 'location' | 'recordedAt'>
): string | undefined {
    const date = readout.recordedAt === undefined ? undefined : eventDate(readout.recordedAt);
    const location = readout.location?.trim() === '' ? undefined : readout.location;
    const parts = [date, location].filter((part) => part !== undefined);

    return parts.length === 0 ? undefined : parts.join(', ');
}

/**
 * Write a reported date in the design's UTC format.
 *
 * @param date - the reported date
 * @returns the date as MM/DD/YYYY HH:mm UTC
 */
function eventDate(date: Date): string {
    const parts = EVENT_DATE_FORMAT.formatToParts(date);
    const values = Object.fromEntries(parts.map((part) => [part.type, part.value]));

    return `${values.month}/${values.day}/${values.year} ${values.hour}:${values.minute} UTC`;
}
